import { useState } from 'react';

const BLUE = '#2563EB';

const ASSISTANT_PROMPTS = [
  'Analyze my sales trend and traffic',
  'Track my product-level performance',
  'Analyze my inventory and restock needs',
  'Compare FBA and FBM Fees',
  'Identify my top listing issues',
];

function HistoryIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
      <path d="M3 3v5h5" />
      <path d="M12 7v5l3 3" />
    </svg>
  );
}

function NewChatIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <line x1="12" y1="8" x2="12" y2="13" />
      <line x1="9.5" y1="10.5" x2="14.5" y2="10.5" />
    </svg>
  );
}

function AttachmentIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21.44 11.05l-9.19 9.19a5 5 0 0 1-7.07-7.07l9.19-9.19a3.5 3.5 0 1 1 4.95 4.95L10.13 17.12a2 2 0 1 1-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

function SendIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

function BotAvatarIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#2B303B"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="8" width="18" height="12" rx="2" />
      <path d="M12 3v5" />
      <circle cx="8" cy="14" r="1" />
      <circle cx="16" cy="14" r="1" />
    </svg>
  );
}

export default function AssistantPanel(): JSX.Element {
  const [input, setInput] = useState('');
  const hasInput = input.trim().length > 0;

  return (
    <div
      style={{
        width: 340,
        height: '100%',
        flexShrink: 0,
        background: '#fff',
        border: '1px solid #E9EBED',
        borderRadius: 8,
        boxShadow: '0 1px 2px rgba(0,7,22,0.1)',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '24px 20px',
          flexShrink: 0,
        }}
      >
        <span style={{ fontSize: 20, fontWeight: 600, lineHeight: '120%', color: '#0F141A' }}>
          STAC Assistant
        </span>
        <div style={{ display: 'flex', gap: 16 }}>
          <button
            type="button"
            aria-label="History"
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: '#2B303B',
              display: 'flex',
            }}
          >
            <HistoryIcon />
          </button>
          <button
            type="button"
            aria-label="New chat"
            onClick={() => setInput('')}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: '#2B303B',
              display: 'flex',
            }}
          >
            <NewChatIcon />
          </button>
        </div>
      </div>
      <div style={{ height: 1, background: '#E9EBED', flexShrink: 0 }} />
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px 20px',
          display: 'flex',
          gap: 12,
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            border: '1px solid #E9EBED',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <BotAvatarIcon />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 600,
                lineHeight: '120%',
                color: '#0F141A',
                marginBottom: 8,
              }}
            >
              STAC Assistant
            </div>
            <div style={{ fontSize: 16, fontWeight: 400, lineHeight: '150%', color: '#0F141A' }}>
              Hi, I'm your STAC Assistant — here to help grow your business with data-driven
              insights and expert guidance.
            </div>
          </div>
          <div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 600,
                lineHeight: '120%',
                color: '#0F141A',
                marginBottom: 8,
              }}
            >
              Ask me anything about selling
            </div>
            <div style={{ fontSize: 16, fontWeight: 400, lineHeight: '150%', color: '#0F141A' }}>
              or select a topic below to get started:
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {ASSISTANT_PROMPTS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setInput(p)}
                style={{
                  textAlign: 'left',
                  padding: '8px 16px',
                  border: '1px solid #D3D5D8',
                  borderRadius: 8,
                  background: '#fff',
                  fontSize: 14,
                  fontWeight: 500,
                  lineHeight: '150%',
                  color: '#298296',
                  cursor: 'pointer',
                }}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div style={{ height: 1, background: '#E9EBED', flexShrink: 0 }} />
      <div
        style={{
          padding: '12px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexShrink: 0,
        }}
      >
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: '#F5F7FA',
            borderRadius: 8,
            padding: '7px 7px 7px 12px',
          }}
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask anything..."
            style={{
              flex: 1,
              minWidth: 0,
              border: 'none',
              background: 'none',
              outline: 'none',
              fontSize: 16,
              fontWeight: 500,
              letterSpacing: '-0.011em',
              color: '#0E121B',
            }}
          />
          <span style={{ color: '#717784', display: 'flex', flexShrink: 0 }}>
            <AttachmentIcon />
          </span>
        </div>
        <button
          type="button"
          disabled={!hasInput}
          aria-label="Send"
          onClick={() => setInput('')}
          style={{
            width: 32,
            height: 32,
            flexShrink: 0,
            borderRadius: 8,
            border: 'none',
            background: hasInput ? BLUE : '#94A3B8',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: hasInput ? 'pointer' : 'not-allowed',
          }}
        >
          <SendIcon />
        </button>
      </div>
    </div>
  );
}
